/**
 * Account side of the advertising opt-out (CCPA/CPRA "Do Not Sell or Share"):
 * writes user_privacy_settings.ad_data_opt_out for the signed-in member
 * through the set_my_ad_data_opt_out RPC, so server-side sends (Stripe and
 * PayPal webhooks, renewals, track-event fanout) honor the choice after the
 * person has left the page. See supabase/functions/_shared/adDataPolicy.ts.
 *
 * Same write pattern as productAnalyticsAccountSync.ts: one write at a time
 * in the order made, a pending marker written before each call, and a retry
 * with backoff on sign-in, focus and reconnect for the same member.
 */
import { supabase } from '@/integrations/supabase/client';

export type AdDataOptOutSource = 'banner' | 'privacy_choices' | 'gpc' | 'native';

/**
 * Pending account writes, one entry per member (a JSON object keyed by user
 * id), so one member's write never overwrites another's pending opt-out.
 */
export const AD_DATA_ACCOUNT_SYNC_PENDING_KEY = 'jp_ad_data_account_sync_pending';
/**
 * The last explicit ad data choice made on this device while signed out,
 * with its time, settled against the account at the next sign-in.
 */
export const AD_DATA_SIGNED_OUT_CHOICE_KEY = 'jp_ad_data_signed_out_choice';
/** Marks that this device's opt-out (GPC or an explicit one) is on the account. */
const AD_DATA_DEVICE_SYNCED_PREFIX = 'jp_ad_data_device_synced:';

const ACCOUNT_SYNC_BASE_DELAY_MS = 30_000;
const ACCOUNT_SYNC_MAX_DELAY_MS = 60 * 60 * 1000;

interface PendingAdDataSync {
  userId: string;
  optOut: boolean;
  source: AdDataOptOutSource;
  attempts: number;
  nextAttemptAt: number;
}

const SOURCES: readonly AdDataOptOutSource[] = ['banner', 'privacy_choices', 'gpc', 'native'];

function isSource(value: unknown): value is AdDataOptOutSource {
  return typeof value === 'string' && (SOURCES as readonly string[]).includes(value);
}

function parsePendingEntry(raw: unknown, userId: string): PendingAdDataSync | null {
  if (!raw || typeof raw !== 'object') return null;
  const parsed = raw as Partial<PendingAdDataSync>;
  if (typeof parsed.optOut !== 'boolean' || !isSource(parsed.source)) return null;
  return {
    userId,
    optOut: parsed.optOut,
    source: parsed.source,
    attempts: typeof parsed.attempts === 'number' ? parsed.attempts : 0,
    nextAttemptAt: typeof parsed.nextAttemptAt === 'number' ? parsed.nextAttemptAt : 0,
  };
}

/** Every member's pending write on this device, keyed by user id. */
function readPendingMap(): Record<string, PendingAdDataSync> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(AD_DATA_ACCOUNT_SYNC_PENDING_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (!parsed || typeof parsed !== 'object') return {};
    // Older single-entry shape: { userId, optOut, ... }.
    if (typeof parsed.userId === 'string') {
      const legacy = parsePendingEntry(parsed, parsed.userId);
      return legacy ? { [legacy.userId]: legacy } : {};
    }
    const map: Record<string, PendingAdDataSync> = {};
    for (const [userId, value] of Object.entries(parsed)) {
      const entry = parsePendingEntry(value, userId);
      if (entry) map[userId] = entry;
    }
    return map;
  } catch {
    return {};
  }
}

function writePendingMap(map: Record<string, PendingAdDataSync>): void {
  if (typeof window === 'undefined') return;
  try {
    if (Object.keys(map).length === 0) {
      window.localStorage.removeItem(AD_DATA_ACCOUNT_SYNC_PENDING_KEY);
    } else {
      window.localStorage.setItem(AD_DATA_ACCOUNT_SYNC_PENDING_KEY, JSON.stringify(map));
    }
  } catch {
    // Storage blocked: the next explicit choice or sign-in pushes again.
  }
}

function readPending(userId: string): PendingAdDataSync | null {
  return readPendingMap()[userId] ?? null;
}

/** Sets or clears one member's pending write; other members' entries stay. */
function writePending(userId: string, pending: PendingAdDataSync | null): void {
  const map = readPendingMap();
  if (pending) map[userId] = { ...pending, userId };
  else delete map[userId];
  writePendingMap(map);
}

interface SignedOutAdDataChoice {
  optOut: boolean;
  source: AdDataOptOutSource;
  /** Epoch ms when the choice was made. */
  at: number;
}

function readSignedOutChoice(): SignedOutAdDataChoice | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(AD_DATA_SIGNED_OUT_CHOICE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SignedOutAdDataChoice> | null;
    if (!parsed || typeof parsed.optOut !== 'boolean' || !isSource(parsed.source) || typeof parsed.at !== 'number') {
      return null;
    }
    return { optOut: parsed.optOut, source: parsed.source, at: parsed.at };
  } catch {
    return null;
  }
}

function writeSignedOutChoice(choice: SignedOutAdDataChoice | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (choice) window.localStorage.setItem(AD_DATA_SIGNED_OUT_CHOICE_KEY, JSON.stringify(choice));
    else window.localStorage.removeItem(AD_DATA_SIGNED_OUT_CHOICE_KEY);
  } catch {
    // Storage blocked: the choice still applies on this device.
  }
}

function hasGpcSignal(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

function readDeviceSynced(userId: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(`${AD_DATA_DEVICE_SYNCED_PREFIX}${userId}`);
  } catch {
    return null;
  }
}

function writeDeviceSynced(userId: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    const key = `${AD_DATA_DEVICE_SYNCED_PREFIX}${userId}`;
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // Storage blocked: the next sign-in pushes again, which is harmless.
  }
}

function backoffDelay(attempts: number): number {
  return Math.min(ACCOUNT_SYNC_BASE_DELAY_MS * 2 ** Math.max(0, attempts - 1), ACCOUNT_SYNC_MAX_DELAY_MS);
}

async function readSessionUserId(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.id ?? null;
  } catch {
    return null;
  }
}

/** Asks the server to clear RevenueCat's ad identifiers for the signed-in member. Never throws. */
function requestServerAdIdentityClear(): void {
  try {
    void Promise.resolve(supabase.functions.invoke('apply-ad-data-opt-out', { body: {} })).catch(
      () => undefined
    );
  } catch {
    // Best-effort; the RevenueCat webhook clears the identifiers too.
  }
}

let writeQueue: Promise<unknown> = Promise.resolve();

function sendAdDataOptOut(
  userId: string,
  optOut: boolean,
  source: AdDataOptOutSource,
  attempts: number
): Promise<boolean> {
  const run = writeQueue.then(
    () => sendAdDataOptOutNow(userId, optOut, source, attempts),
    () => sendAdDataOptOutNow(userId, optOut, source, attempts)
  );
  writeQueue = run.catch(() => false);
  return run;
}

async function sendAdDataOptOutNow(
  userId: string,
  optOut: boolean,
  source: AdDataOptOutSource,
  attempts: number
): Promise<boolean> {
  // Recorded before the call, so a crash or an offline device retries later.
  writePending(userId, { userId, optOut, source, attempts, nextAttemptAt: Date.now() + backoffDelay(attempts + 1) });
  try {
    // Someone else signed in meanwhile: keep the choice pending for its owner.
    if ((await readSessionUserId()) !== userId) return false;
    const { error } = await supabase.rpc('set_my_ad_data_opt_out', {
      p_opt_out: optOut,
      p_source: source,
      p_expected_user: userId,
    });
    if (error) throw new Error(error.message);
    const pending = readPending(userId);
    if (pending && pending.optOut === optOut) writePending(userId, null);
    // An opt-out also clears the ad identifiers RevenueCat holds for this
    // member, so its AppsFlyer integration stops now. Best-effort: the server
    // re-checks the account, and the RevenueCat webhook clears them again.
    if (optOut) requestServerAdIdentityClear();
    return true;
  } catch (error) {
    const nextAttempts = attempts + 1;
    const pending = readPending(userId);
    // A newer choice by this member replaced this one while it was in flight.
    if (!pending || pending.optOut === optOut) {
      writePending(userId, {
        userId,
        optOut,
        source,
        attempts: nextAttempts,
        nextAttemptAt: Date.now() + backoffDelay(nextAttempts),
      });
    }
    if (import.meta.env.DEV) console.warn('Failed to record the ad data choice on the account:', error);
    return false;
  }
}

/**
 * Records the ad data choice on the signed-in account. `optOut: true` for a
 * banner Decline, a Your Privacy Choices opt-out or a GPC signal; `false` for
 * an explicit later opt-in. Signed out: nothing to record (sign-in pushes the
 * device's opt-out then). Never throws.
 */
export async function pushAccountAdDataOptOut(
  optOut: boolean,
  source: AdDataOptOutSource
): Promise<boolean> {
  // Any account read already in flight predates this choice.
  choiceEpoch += 1;
  const userId = await readSessionUserId();
  if (!userId) {
    // Signed out: keep the choice with its time; the next sign-in settles it
    // against the account's own (newer one wins).
    writeSignedOutChoice({ optOut, source, at: Date.now() });
    return false;
  }
  // Made while signed in: nothing left to settle from a signed-out choice.
  writeSignedOutChoice(null);
  // This device's explicit choice is the account's value from now on.
  accountResolutionCache.set(userId, optOut);
  // An explicit opt-in clears the "device opt-out already pushed" marker, so
  // a later opt-out on this device is pushed again.
  if (!optOut) writeDeviceSynced(userId, null);
  const ok = await sendAdDataOptOut(userId, optOut, source, 0);
  if (ok && optOut) writeDeviceSynced(userId, source);
  return ok;
}

/**
 * Retries the signed-in member's own pending choice (other members' entries
 * wait for their own sign-in). True when nothing is left pending for them.
 */
export async function retryPendingAccountAdDataSync(options?: { force?: boolean }): Promise<boolean> {
  const userId = await readSessionUserId();
  if (!userId) return Object.keys(readPendingMap()).length === 0;
  const pending = readPending(userId);
  if (!pending) return true;
  if (!options?.force && Date.now() < pending.nextAttemptAt) return false;
  return sendAdDataOptOut(userId, pending.optOut, pending.source, pending.attempts);
}

export type AccountAdDataOptOutRead = 'opted_out' | 'not_opted_out' | 'error';

interface AccountAdDataOptOutRow {
  result: AccountAdDataOptOutRead;
  /** When the account opted out (null when not opted out or unknown). */
  optedOutAt: number | null;
}

/**
 * Reads user_privacy_settings.ad_data_opt_out for the signed-in member: the
 * same owner-only read (and RLS) as the analytics opt-out read in
 * productAnalyticsAccountSync.ts. 'error' never counts as opted out here;
 * the caller leaves the device unchanged and retries.
 */
export async function readAccountAdDataOptOut(userId: string): Promise<AccountAdDataOptOutRead> {
  return (await readAccountAdDataOptOutRow(userId)).result;
}

async function readAccountAdDataOptOutRow(userId: string): Promise<AccountAdDataOptOutRow> {
  try {
    const { data, error } = await supabase
      .from('user_privacy_settings')
      .select('ad_data_opt_out, ad_data_opt_out_at')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) return { result: 'error', optedOutAt: null };
    if (data?.ad_data_opt_out !== true) return { result: 'not_opted_out', optedOutAt: null };
    const at = typeof data.ad_data_opt_out_at === 'string' ? Date.parse(data.ad_data_opt_out_at) : NaN;
    return { result: 'opted_out', optedOutAt: Number.isFinite(at) ? at : null };
  } catch {
    return { result: 'error', optedOutAt: null };
  }
}

/**
 * Settles a choice made on this device while signed out against the account
 * at sign-in. The newer one wins: a newer device Accept writes false to the
 * account (never on a GPC browser) and stops the account opt-out applying
 * here; a newer account opt-out wins over the device. A device opt-out is
 * uploaded by syncDeviceAdDataOptOutToAccount. Returns the effective account
 * opt-out for this device.
 */
function settleSignedOutChoice(userId: string, row: AccountAdDataOptOutRow): boolean {
  const accountOptedOut = row.result === 'opted_out';
  const choice = readSignedOutChoice();
  if (!choice) return accountOptedOut;
  writeSignedOutChoice(null);
  if (choice.optOut) return accountOptedOut;
  const accountNewer = accountOptedOut && row.optedOutAt !== null && row.optedOutAt > choice.at;
  if (accountNewer) return true;
  if (hasGpcSignal()) return accountOptedOut;
  writeDeviceSynced(userId, null);
  void sendAdDataOptOut(userId, false, choice.source, 0).catch(() => false);
  return false;
}

export type AccountAdDataResolution = 'opted_out' | 'not_opted_out' | 'unknown';

/** Successful account reads this page session, by member. */
const accountResolutionCache = new Map<string, boolean>();
const inflightAccountReads = new Map<string, Promise<AccountAdDataOptOutRead>>();

interface AccountReadRetry {
  userId: string;
  onOptedOut: () => void;
}

/** A failed account read waiting for the next focus, reconnect or sign-in. */
let accountReadRetry: AccountReadRetry | null = null;

// Only the newest read for a member may change state. Each launch gets the
// next generation for that member, and any explicit choice on this device
// bumps the choice epoch, so a slow read that started before an Accept (or a
// newer retry) can't reapply an opt-out when it finally lands.
const accountReadGeneration = new Map<string, number>();
let choiceEpoch = 0;

function cachedReadResult(userId: string): AccountAdDataOptOutRead {
  const cached = accountResolutionCache.get(userId);
  if (cached === undefined) return 'error';
  return cached ? 'opted_out' : 'not_opted_out';
}

/** The account value already read this session (undefined = not read yet). */
export function getCachedAccountAdDataOptOut(userId: string): boolean | undefined {
  return accountResolutionCache.get(userId);
}

function startAccountRead(userId: string, onOptedOut: () => void): Promise<AccountAdDataOptOutRead> {
  const existing = inflightAccountReads.get(userId);
  if (existing) return existing;
  const generation = (accountReadGeneration.get(userId) ?? 0) + 1;
  accountReadGeneration.set(userId, generation);
  const epochAtLaunch = choiceEpoch;
  const read: Promise<AccountAdDataOptOutRead> = readAccountAdDataOptOutRow(userId).then((row): AccountAdDataOptOutRead => {
    if (inflightAccountReads.get(userId) === read) inflightAccountReads.delete(userId);
    // Superseded by a newer read or a newer choice on this device: report
    // what is known now and change nothing.
    if (accountReadGeneration.get(userId) !== generation || choiceEpoch !== epochAtLaunch) {
      return cachedReadResult(userId);
    }
    if (row.result === 'error') {
      accountReadRetry = { userId, onOptedOut };
      return 'error';
    }
    if (accountReadRetry?.userId === userId) accountReadRetry = null;
    let optedOut = settleSignedOutChoice(userId, row);
    // A newer opt-in on this device that has not reached the account yet wins.
    const pending = readPending(userId);
    if (optedOut && pending && !pending.optOut) optedOut = false;
    accountResolutionCache.set(userId, optedOut);
    // Applied even when the answer arrives after the caller stopped waiting.
    if (optedOut) onOptedOut();
    return optedOut ? 'opted_out' : 'not_opted_out';
  });
  inflightAccountReads.set(userId, read);
  return read;
}

/**
 * The member's account ad opt-out, waiting at most `timeoutMs`. 'unknown'
 * (read error or timeout) never counts as opted out on the device: callers
 * skip ad identity setup for this session only, and the read is retried on
 * focus, reconnect and the next sign-in. `onOptedOut` runs whenever a read
 * finds the opt-out, including one that lands after the timeout.
 */
export async function resolveAccountAdDataOptOut(
  userId: string,
  onOptedOut: () => void,
  timeoutMs = 2000
): Promise<AccountAdDataResolution> {
  const cached = accountResolutionCache.get(userId);
  if (cached !== undefined) return cached ? 'opted_out' : 'not_opted_out';
  const read = startAccountRead(userId, onOptedOut);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), timeoutMs);
  });
  const outcome = await Promise.race([read, timeout]);
  if (timer !== undefined) clearTimeout(timer);
  if (outcome === 'timeout') {
    // Never reuse a read that may never settle: the next focus, reconnect or
    // sign-in starts a fresh one. A late answer from this one still applies.
    if (inflightAccountReads.get(userId) === read) inflightAccountReads.delete(userId);
    accountReadRetry = { userId, onOptedOut };
    return 'unknown';
  }
  if (outcome === 'error') return 'unknown';
  return outcome;
}

/**
 * After sign-in: retry anything pending, then upload this device's own
 * opt-out to the account. Only an explicit choice made on this device
 * (banner, Privacy Choices, the app's toggle) or a GPC signal is uploaded,
 * with its source, once per member per device; an opt-out that came from an
 * account (another member's, on a shared device) never is. GPC wins over a
 * stored opt-in for this browser.
 */
export async function syncDeviceAdDataOptOutToAccount(
  userId: string,
  device: { gpc: boolean; optOutSource: AdDataOptOutSource | null }
): Promise<void> {
  if (!userId) return;
  await retryPendingAccountAdDataSync({ force: true });
  const source: AdDataOptOutSource | null = device.gpc ? 'gpc' : device.optOutSource;
  if (!source) return;
  if (readDeviceSynced(userId) === source) return;
  if ((await readSessionUserId()) !== userId) return;
  const ok = await sendAdDataOptOut(userId, true, source, 0);
  if (ok) writeDeviceSynced(userId, source);
}

/** Retries a failed account read for the member it was for. */
async function retryAccountRead(): Promise<void> {
  const retry = accountReadRetry;
  if (!retry) return;
  if ((await readSessionUserId()) !== retry.userId) return;
  // Bounded like the first read: a hung retry is evicted and retried again.
  await resolveAccountAdDataOptOut(retry.userId, retry.onOptedOut);
}

let retryAttached = false;

/** Retries pending account writes and failed reads now and on focus and reconnect. */
export function startAccountAdDataSyncRetry(): void {
  void retryPendingAccountAdDataSync();
  if (retryAttached || typeof window === 'undefined') return;
  const retry = () => {
    void retryPendingAccountAdDataSync();
    void retryAccountRead();
  };
  window.addEventListener('focus', retry);
  window.addEventListener('online', retry);
  retryAttached = true;
}

/** Test hook: forget this session's account reads and retries. */
export function resetAccountAdDataSyncForTests(): void {
  accountResolutionCache.clear();
  inflightAccountReads.clear();
  accountReadGeneration.clear();
  choiceEpoch = 0;
  accountReadRetry = null;
}
